import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  model,
  input,
  effect
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take, takeUntil } from 'rxjs/operators';

export interface InfiniteSelectFetchParams {
  page: number;
  per_page: number;
  search?: string;
  tipo?: number;
}

export interface InfiniteSelectMeta {
  per_page?: number;
  total_entries?: number;
  current_page?: number;
}

export interface InfiniteSelectPage<T> {
  data: T[];
  meta?: InfiniteSelectMeta;
}

@Component({
  selector: 'app-infinite-select',
  templateUrl: './infinite-select.component.html',
  styleUrls: ['./infinite-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InfiniteSelectComponent),
      multi: true
    }
  ]
})
export class InfiniteSelectComponent<T = any> implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  location = input<string>('tanks');
  settlementType = input<number | null>();
  @Input() fetchPage?: (params: InfiniteSelectFetchParams) => Observable<InfiniteSelectPage<T>>;
  @Input() pageSize = 10;
  @Input() labelKey = 'name';
  @Input() valueKey = 'id';
  @Input() placeholder = 'Selecciona...';
  @Input() allOptionLabel?: string;
  @Input() allOptionValue: any = 0;
  @Input() panelMaxHeight = 260;
  @Input() triggerClass = 'form-select';
  @Input() panelClass = '';
  @Input() showCaret = true;
  @Input() searchEnabled = false;
  @Input() searchPlaceholder = 'Buscar...';
  @Input() minSearchLength = 0;
  @Input() preload = true;
  @Input() seedItems: T[] = [];
  @Input() disabled = false;
  selectedSettlement = model<any>({});

  @Output() selectionChange = new EventEmitter<any>();
  @Output() itemsChange = new EventEmitter<T[]>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  items: T[] = [];
  isOpen = false;
  loading = false;
  hasMore = true;
  errorMessage: string | null = null;

  selectedValue: any = undefined;
  selectedLabel = '';
  searchTerm = '';

  private nextPage = 1;
  private itemKeys = new Set<any>();
  private requestId = 0;
  private destroy$ = new Subject<void>();
  private search$ = new Subject<string>();
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private host: ElementRef, private cdr: ChangeDetectorRef) {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.searchTerm = term;
        this.resetList();
        this.loadNextPage();
      });
    
    

    effect(() => {
      const settlementType = this.settlementType();
      if (this.settlementTypeValid()) {
        this.reset();
        this.nextPage = 1;
        this.loadNextPage();
      }
    });
  }

  ngOnInit(): void {
    if (this.preload && this.fetchPage) {
      this.resetList();
      this.loadNextPage();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['seedItems']) return;
    const added = this.applySeedItems();
    if (added) {
      this.itemsChange.emit([...this.items]);
      this.cdr.markForCheck();
    }
  }

  settlementTypeValid(): boolean {
    return [0, 1].some(e => e === this.settlementType())
  }

  writeValue(value: any): void {
    this.selectedValue = value;
    if ([undefined, null, ''].includes(value)) {
      this.selectedLabel = '';
      this.selectedSettlement.set(null);
      this.cdr.markForCheck();
      return;
    }

    this.selectedLabel = this.resolveLabelFromValue(value) || '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.opened.emit();
      if (this.items.length === 0) {
        this.resetList();
        this.loadNextPage();
      }
    } else {
      this.onTouched();
      this.closed.emit();
    }
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.onTouched();
    this.closed.emit();
    this.cdr.markForCheck();
  }

  reset(): void {
    this.resetList();
    if (this.allOptionLabel !== undefined) {
      this.selectedValue = this.allOptionValue;
      this.selectedLabel = this.allOptionLabel;
    } else {
      this.selectedValue = undefined;
      this.selectedLabel = '';
    }
    this.cdr.markForCheck();
  }

  onPanelScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;
    const threshold = 24;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - threshold) {
      this.loadNextPage();
    }
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target?.value || '';
    if (value.length < this.minSearchLength) {
      if (this.searchTerm !== '') {
        this.search$.next('');
      }
      return;
    }
    this.search$.next(value);
  }

  selectItem(item: T): void {
    const value = this.resolveValue(item);
    this.selectedValue = value;
    this.selectedLabel = this.resolveLabel(item);
    this.onChange(value);
    this.selectionChange.emit(value);
    this.close();
    this.selectedSettlement.update(() => item);
  }

  selectValue(value: any): void {
    this.selectedValue = value;
    this.selectedLabel = this.allOptionLabel || '';
    this.onChange(value);
    this.selectionChange.emit(value);
    this.close();
  }

  isSelected(item: T): boolean {
    const value = this.resolveValue(item);
    return value === this.selectedValue;
  }

  get displayLabel(): string {
    if (this.selectedLabel) return this.selectedLabel;
    if (this.selectedValue === undefined && this.allOptionLabel) return this.allOptionLabel;
    if (this.selectedValue === this.allOptionValue && this.allOptionLabel) return this.allOptionLabel;
    return '';
  }

  trackByItem = (_index: number, item: T): any => {
    const key = this.resolveValue(item);
    return key !== undefined ? key : item;
  };

  retry(): void {
    this.errorMessage = null;
    this.loadNextPage();
  }

  private resetList(): void {
    this.items = [];
    this.itemKeys.clear();
    this.nextPage = 1;
    this.hasMore = true;
    this.errorMessage = null;
    this.loading = false;
    this.requestId += 1;
    this.applySeedItems();
    this.itemsChange.emit([...this.items]);
    this.cdr.markForCheck();
  }

  private loadNextPage(): void {
    if (this.loading || !this.hasMore || !this.fetchPage) return;
    this.loading = true;
    this.cdr.markForCheck();
    const requestId = this.requestId;
    const params: InfiniteSelectFetchParams = {
      page: this.nextPage,
      per_page: this.pageSize
    };
    if (this.settlementTypeValid()) {
      params.tipo = this.settlementType()!;
    }
    if (this.searchTerm) {
      params.search = this.searchTerm;
    }

    this.fetchPage(params)
      .pipe(
        take(1),
        finalize(() => {
          if (requestId !== this.requestId) return;
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (result) => {
          if (requestId !== this.requestId) return;
          const data = result?.data || [];
          this.mergeItems(data, false);

          const meta = result?.meta;
          const perPage = meta?.per_page ?? this.pageSize;
          const total = meta?.total_entries;
          const currentPage = meta?.current_page ?? this.nextPage;

          if (typeof total === 'number') {
            const totalPages = Math.ceil(total / perPage);
            this.hasMore = currentPage < totalPages;
          } else {
            this.hasMore = data.length >= perPage;
          }

          this.nextPage = currentPage + 1;
          this.updateSelectedLabel();
          this.itemsChange.emit([...this.items]);
          this.cdr.markForCheck();
        },
        error: () => {
          if (requestId !== this.requestId) return;
          this.errorMessage = 'Error al cargar opciones';
          this.cdr.markForCheck();
        }
      });
  }

  private mergeItems(items: T[], prepend: boolean): boolean {
    if (!items || items.length === 0) return false;
    const newItems: T[] = [];
    for (const item of items) {
      const key = this.resolveValue(item);
      if (this.itemKeys.has(key)) continue;
      this.itemKeys.add(key);
      newItems.push(item);
    }
    if (newItems.length === 0) return false;
    this.items = prepend ? [...newItems, ...this.items] : [...this.items, ...newItems];
    return true;
  }

  private applySeedItems(): boolean {
    if (!this.seedItems || this.seedItems.length === 0) return false;
    const added = this.mergeItems(this.seedItems, true);
    if (added) this.updateSelectedLabel();
    return added;
  }

  private updateSelectedLabel(): void {
    if (!this.selectedLabel && this.selectedValue !== undefined && this.selectedValue !== null) {
      const match = this.items.find((item) => this.resolveValue(item) === this.selectedValue);
      if (match) {
        this.selectedSettlement.update(() => match);
        this.selectedLabel = this.resolveLabel(match);
      }
    }
  }

  private resolveValue(item: T): any {
    if (!item || typeof item !== 'object') return item;
    return (item as any)[this.valueKey];
  }

  resolveLabel(item: T): string {
    if (!item) return '';
    if (typeof item !== 'object') return String(item);
    const label = (item as any)[this.labelKey];
    return label !== undefined ? String(label) : '';
  }

  private resolveLabelFromValue(value: any): string {
    if (value === undefined || value === null) return '';
    if (this.allOptionLabel !== undefined && value === this.allOptionValue) {
      return this.allOptionLabel;
    }
    const found = this.items.find((item) => this.resolveValue(item) === value);
    return found ? this.resolveLabel(found) : '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as HTMLElement;
    if (!this.host.nativeElement.contains(target)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }
}
